// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, FileText, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import { SessionExpiredError } from '../../utils/sessionExpiredError';
import './TBConverter.css';

const TBConverterClassification = ({ 
  analysisData, 
  existingClassification, // Stored classification from previous visit
  onClassificationComplete,
  onBack, 
  loading: parentLoading,
  onProcessStart,
  onError,
  hasExistingResults = false,
  onViewResults
}) => {
  const { analysisResult, files } = analysisData;
  
  const [classifying, setClassifying] = useState(false);
  const [classificationResult, setClassificationResult] = useState(existingClassification || null);
  const [selectedStatementType, setSelectedStatementType] = useState('income_statement');
  
  // Ref to prevent duplicate classification calls (React Strict Mode / re-renders)
  const classificationTriggered = useRef(false);
  
  // Auto-trigger classification on mount ONLY if we don't have existing classification
  useEffect(() => {
    if (!classificationResult && !classifying && !existingClassification && !classificationTriggered.current) {
      classificationTriggered.current = true;
      classifyAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update local state if existingClassification prop changes
  useEffect(() => {
    if (existingClassification && !classificationResult) {
      setClassificationResult(existingClassification);
      classificationTriggered.current = true; // Mark as handled
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingClassification]);

  const classifyAccounts = async () => {
    setClassifying(true);
    
    try {
      await supabase.auth.getSession();
      
      // Build sheets config from analysis result
      const sheetsConfig = analysisResult.sheets.map((sheet, idx) => ({
        file_index: sheet.file_index,
        sheet_name: sheet.sheet_name,
        structure: sheet.structure,
        detected_period: sheet.detected_period
      }));
      
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('sheets_config', JSON.stringify(sheetsConfig));

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tb-converter/classify-accounts/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          },
          timeout: 300000, // 5 minutes for classification
        }
      );

      setClassificationResult(response.data);
      logger.info('Account classification complete:', response.data);
      
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Account classification error:', error);
      onError(error.response?.data?.detail || error.message);
    } finally {
      setClassifying(false);
    }
  };

  const handleContinue = () => {
    if (!classificationResult) return;
    
    onClassificationComplete({
      ...analysisData,
      classificationResult,
      selectedStatementType
    });
  };

  const plCount = classificationResult?.income_statement_count || 0;
  const bsCount = classificationResult?.balance_sheet_count || 0;

  return (
    <div className="classification-container">
      <div className="classification-header">
        <div className="header-icon tb-icon">
          <FileText size={24} />
        </div>
        <div>
          <h3>Account Classification</h3>
          <p className="classification-description">
            {existingClassification 
              ? "Your previous classification is preserved. Choose which statement to generate."
              : "Accounts have been classified as P&L or Balance Sheet. Choose which statement to generate."
            }
          </p>
        </div>
      </div>

      {/* Classification Status */}
      {classifying ? (
        <div className="classification-loading">
          <div className="loading-spinner-large"></div>
          <p>Classifying accounts using AI...</p>
          <span className="loading-hint">This may take a moment for large trial balances</span>
        </div>
      ) : classificationResult ? (
        <>
          {/* Statement Type Selection */}
          <div className="statement-type-selection">
            <h4>Select Statement Type</h4>
            <p className="selection-hint">Choose which statement to generate from the trial balance</p>
            
            <div className="statement-options">
              {/* P&L Option */}
              <button
                type="button"
                className={`statement-option ${selectedStatementType === 'income_statement' ? 'selected' : ''}`}
                onClick={() => setSelectedStatementType('income_statement')}
              >
                <div className="option-header">
                  <CheckCircle size={20} className="option-check" />
                  <span className="option-title">Profit & Loss (P&L)</span>
                </div>
                <p className="option-description">
                  Generate an income statement with revenue and expense accounts
                </p>
                <div className="option-count">{plCount} accounts</div>
              </button>

              {/* Balance Sheet Option - Locked */}
              <button
                type="button"
                className="statement-option disabled"
                disabled
              >
                <div className="option-header">
                  <Lock size={20} className="option-lock" />
                  <span className="option-title">Balance Sheet</span>
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
                <p className="option-description">
                  Generate a balance sheet with assets, liabilities, and equity
                </p>
                <div className="option-count">{bsCount} accounts</div>
              </button>
            </div>
          </div>

          {/* All Classified Accounts */}
          {classificationResult?.accounts && classificationResult.accounts.length > 0 && (
            <div className="account-preview">
              <h4>Classified Accounts ({classificationResult.accounts.length} total)</h4>
              <div className="preview-table-container scrollable">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Type</th>
                      <th>Statement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classificationResult.accounts.map((acc, idx) => (
                      <tr key={idx}>
                        <td className="account-name">{acc.account_name}</td>
                        <td>
                          <span className={`type-badge ${acc.account_type}`}>
                            {acc.account_type}
                          </span>
                        </td>
                        <td>
                          <span className={`statement-badge ${acc.statement_type === 'income_statement' ? 'pl' : 'bs'}`}>
                            {acc.statement_type === 'income_statement' ? 'P&L' : 'Balance Sheet'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Re-classify option */}
          {existingClassification && (
            <div className="reclassify-option">
              <button 
                onClick={() => {
                  classificationTriggered.current = true; // Prevent any race conditions
                  classifyAccounts();
                }} 
                className="reclassify-button" 
                disabled={classifying}
              >
                🔄 Re-classify Accounts
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="classification-error">
          <AlertCircle size={24} />
          <p>Classification failed. Please try again.</p>
          <button onClick={classifyAccounts} className="retry-button">
            Retry Classification
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="classification-actions">
        <button 
          className="back-button"
          onClick={onBack}
          disabled={classifying || parentLoading}
        >
          <ArrowLeft size={16} />
          Back to Periods
        </button>
        
        <div className="classification-actions-right">
          {hasExistingResults && onViewResults && (
            <button
              className="view-results-button"
              onClick={onViewResults}
              disabled={classifying || parentLoading}
            >
              View Results
              <ArrowRight size={16} />
            </button>
          )}
          <button
            className="analyze-button btn-animated"
            onClick={handleContinue}
            disabled={classifying || parentLoading || !classificationResult}
          >
            {classifying ? (
              <>
                <div className="button-spinner"></div>
                Classifying...
              </>
            ) : (
              <>
                Continue to Convert
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TBConverterClassification;
